/**
 * Canonical list of doctor specialties — frontend mirror of
 * `MAIE.Backend/src/common/types/specialty.ts`. Labels co-locate here so
 * pickers, filters, and chip displays don't each maintain their own list.
 *
 * Keep `value` strings in lockstep with the backend constant; the backend
 * DTO validates against its own SPECIALTIES array on PATCH /auth/me/profile.
 *
 * `general` is the catch-all / unspecified bucket.
 */

export interface SpecialtyEntry {
  value: string;
  label: { en: string; ar: string };
}

export const SPECIALTIES: ReadonlyArray<SpecialtyEntry> = [
  { value: 'general', label: { en: 'General / Unspecified', ar: 'عام / غير محدد' } },
  { value: 'family-medicine', label: { en: 'Family Medicine', ar: 'طب الأسرة' } },
  { value: 'internal-medicine', label: { en: 'Internal Medicine', ar: 'الطب الباطني' } },
  { value: 'pediatrics', label: { en: 'Pediatrics', ar: 'طب الأطفال' } },
  { value: 'geriatrics', label: { en: 'Geriatrics', ar: 'طب المسنين' } },
  { value: 'emergency', label: { en: 'Emergency Medicine', ar: 'طب الطوارئ' } },
  { value: 'anesthesiology', label: { en: 'Anesthesiology', ar: 'التخدير' } },
  { value: 'critical-care', label: { en: 'Critical Care', ar: 'الرعاية الحرجة' } },
  { value: 'radiology', label: { en: 'Radiology', ar: 'الأشعة' } },
  { value: 'pathology', label: { en: 'Pathology', ar: 'علم الأمراض' } },
  { value: 'cardiology', label: { en: 'Cardiology', ar: 'أمراض القلب' } },
  { value: 'neurology', label: { en: 'Neurology', ar: 'الأعصاب' } },
  { value: 'dermatology', label: { en: 'Dermatology', ar: 'الجلدية' } },
  { value: 'endocrinology', label: { en: 'Endocrinology', ar: 'الغدد الصماء' } },
  { value: 'gastroenterology', label: { en: 'Gastroenterology', ar: 'الجهاز الهضمي' } },
  { value: 'hematology', label: { en: 'Hematology', ar: 'أمراض الدم' } },
  { value: 'infectious-disease', label: { en: 'Infectious Disease', ar: 'الأمراض المعدية' } },
  { value: 'nephrology', label: { en: 'Nephrology', ar: 'أمراض الكلى' } },
  { value: 'oncology', label: { en: 'Oncology', ar: 'الأورام' } },
  { value: 'pulmonology', label: { en: 'Pulmonology', ar: 'الجهاز التنفسي' } },
  { value: 'rheumatology', label: { en: 'Rheumatology', ar: 'الروماتيزم' } },
  { value: 'allergy-immunology', label: { en: 'Allergy & Immunology', ar: 'الحساسية والمناعة' } },
  { value: 'obstetrics-gynecology', label: { en: 'Obstetrics & Gynecology', ar: 'النساء والتوليد' } },
  { value: 'ophthalmology', label: { en: 'Ophthalmology', ar: 'طب العيون' } },
  { value: 'otolaryngology', label: { en: 'ENT (Otolaryngology)', ar: 'الأنف والأذن والحنجرة' } },
  { value: 'urology', label: { en: 'Urology', ar: 'المسالك البولية' } },
  { value: 'psychiatry', label: { en: 'Psychiatry', ar: 'الطب النفسي' } },
  { value: 'surgery', label: { en: 'General Surgery', ar: 'الجراحة العامة' } },
  { value: 'orthopedic-surgery', label: { en: 'Orthopedic Surgery', ar: 'جراحة العظام' } },
  { value: 'neurosurgery', label: { en: 'Neurosurgery', ar: 'جراحة المخ والأعصاب' } },
  { value: 'plastic-surgery', label: { en: 'Plastic Surgery', ar: 'جراحة التجميل' } },
  { value: 'dental', label: { en: 'Dentistry', ar: 'طب الأسنان' } },
  { value: 'physical-medicine-rehabilitation', label: { en: 'Physical Medicine & Rehabilitation', ar: 'الطب الطبيعي والتأهيل' } },
];

export type Specialty = (typeof SPECIALTIES)[number]['value'];

export const SPECIALTY_VALUES: ReadonlyArray<string> = SPECIALTIES.map((s) => s.value);

export function getSpecialtyLabel(
  value: string | null | undefined,
  language: 'en' | 'ar' = 'en',
): string {
  if (!value) return '';
  const found = SPECIALTIES.find((s) => s.value === value);
  return found ? found.label[language] : value;
}

export function isSpecialty(value: unknown): value is Specialty {
  return typeof value === 'string' && SPECIALTY_VALUES.includes(value);
}
